import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  getSaveStatus,
  subscribeSaveStatus,
  type SaveStatus,
} from '../storage/saveStatus';

/**
 * Global banner shown when local saves are failing. Auto-clears as soon as a
 * save succeeds again (the store flips back to `ok`), so it is intentionally
 * not dismissable — if it's showing, the user's progress really isn't being
 * persisted and they should back up.
 */
export default function SaveErrorBanner() {
  const { t } = useTranslation('app');
  const [status, setStatus] = useState<SaveStatus>(getSaveStatus);

  useEffect(() => subscribeSaveStatus(() => setStatus(getSaveStatus())), []);

  if (status.ok) return null;

  const message =
    status.kind === 'quota'
      ? t(
          'saveError.quota',
          "Your device is out of storage, so your knitting isn't being saved. Free up space or back up your data now.",
        )
      : t(
          'saveError.unknown',
          "Knit in Time can't save right now, so recent changes may be lost. Back up your data to be safe.",
        );

  return (
    <div
      role="alert"
      aria-label={t('saveError.aria', 'Saving failed')}
      className="fixed top-0 left-0 right-0 z-[60] flex flex-col gap-2 border-b border-danger bg-danger-soft p-4 pt-[max(1rem,env(safe-area-inset-top))] shadow-lg sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm font-semibold text-ink">{message}</p>
      <Link
        to="/settings"
        className="shrink-0 self-start rounded-full bg-danger px-4 py-2 text-sm font-bold text-white sm:self-auto"
      >
        {t('saveError.action', 'Back up your data')}
      </Link>
    </div>
  );
}
